import { TransProvider } from "@mbarzda/solid-i18next"
import { Route, type RouteSectionProps, useNavigate } from "@solidjs/router"
import i18next from "i18next"
import { type Component, createEffect, createSignal, onMount, Show } from "solid-js"
import LoadingScreen from "./app/LoadingScreen"
import Thanks from "./app/thanks/Thanks"
import translations from "./assets/locales/translations.json"
import { MainModule } from "./features/main-module"
import { AppRoutes } from "./shared/app-routes"
import { useNumberFormatter } from "./shared/helpers/currency-formatter"
import { loadProducts, OFFERING_RETRY_POLICY } from "./shared/helpers/load-products"
import { CartProvider } from "./shared/providers/cart.provider"
import { PaddleProvider, usePaddle } from "./shared/providers/paddle.provider"
import { ProductsProvider, useProducts } from "./shared/providers/products.provider"
import { navigateToFirstRemainingProduct } from "./shared/helpers/navigate-to-first-remaining-product"
import Webinar from "./app/webinar/Webinar"
import WebinarB from "./app/webinar-b/WebinarB"

const App: Component<RouteSectionProps> = (props) => {
  const [isLoading, setIsLoading] = createSignal(true)

  const { ensurePaddle } = usePaddle()
  const { currencyFormatter: initLocale } = useNumberFormatter()

  const { addNewProduct } = useProducts()
  const getOffering = MainModule.getPaymentComponent().provideGetOffering()

  onMount(async () => {
    setIsLoading(true)
    initLocale()
    await ensurePaddle({ reportFailure: false })
    await loadProducts(getOffering, addNewProduct, OFFERING_RETRY_POLICY)
    setIsLoading(false)
  })

  createEffect(async () => {
    await i18next.init({
      lng: "en",
      fallbackLng: "en",
      interpolation: {
        escapeValue: true,
      },
      resources: Object.fromEntries(
        Object.entries(translations).map(([lang, translation]) => [lang, { translation }])
      ),
    })
  })

  navigateToFirstRemainingProduct(useNavigate())

  return (
    <Show when={!isLoading()} fallback={<LoadingScreen />}>
      <TransProvider>
        <PaddleProvider>
          <ProductsProvider>
            <CartProvider>
              {props.children}
            </CartProvider>
          </ProductsProvider>
        </PaddleProvider>
      </TransProvider>
    </Show>
  )
}

export const AppRouteTable: Component = () => (
  <>
    <Route path={AppRoutes.Webinar} component={Webinar} />
    <Route path={AppRoutes.WebinarB} component={WebinarB} />
    <Route path={AppRoutes.Thanks} component={Thanks} />
  </>
)

export default App
